import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const serviceLinks = [
  { label: 'Website Design', href: '/website-design' },
  { label: 'Videography', href: '/videography' },
  { label: 'Video Editing', href: '/video-editing' },
  { label: 'Ethical Hacking', href: '/ethical-hacking' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/d7da4bfde00f775727e3be13864eec4a.png"
              alt="Bellport Branding - Long Island Website Design"
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <button
              onClick={() => scrollToSection('about')}
              className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              About
            </button>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                Services
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-xl border border-neutral-100 py-2 z-50">
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      onClick={() => setServicesOpen(false)}
                      className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors cursor-pointer"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/portfolio" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors cursor-pointer whitespace-nowrap">
              Portfolio
            </Link>

            <button
              onClick={() => scrollToSection('start-project')}
              className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact
            </button>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+16316407888"
              className="flex items-center gap-2 text-neutral-900 hover:text-[#1E3A8A] transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-full bg-[#1E3A8A] flex items-center justify-center">
                <i className="ri-phone-fill text-white text-sm"></i>
              </span>
              <span className="font-semibold">(631) 640-7888</span>
            </a>
            <button
              onClick={() => scrollToSection('start-project')}
              className="bg-neutral-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Start a Project
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-neutral-700 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-neutral-100">
          <div className="px-6 py-4 space-y-3">
            <a
              href="tel:+16316407888"
              className="flex items-center gap-2 text-lg font-semibold text-[#1E3A8A] py-2 cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              (631) 640-7888
            </a>
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer"
            >
              Home
            </Link>
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer"
            >
              About
            </button>

            {/* Mobile Services */}
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="flex items-center gap-1 w-full text-left text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer"
              >
                Services
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-sm text-neutral-500 hover:text-neutral-900 py-1.5 cursor-pointer"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/portfolio"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer"
            >
              Portfolio
            </Link>

            <button
              onClick={() => scrollToSection('start-project')}
              className="block w-full text-left text-sm font-medium text-neutral-600 hover:text-neutral-900 py-2 cursor-pointer"
            >
              Contact
            </button>
            <button
              onClick={() => scrollToSection('start-project')}
              className="block w-full bg-neutral-900 text-white px-5 py-3 rounded-full text-sm font-medium text-center cursor-pointer whitespace-nowrap mt-4"
            >
              Start a Project
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}